import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ArrowLeft, User, Shield, Bell, Users as PeopleIcon, Eye, HelpCircle,
  BookOpen, FileText, LogOut, Trash2, Loader2, Languages,
} from "lucide-react";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Settings hub — long, scannable menu modelled after Yubo's settings list.
 * Each row links to an existing screen, opens a confirmation modal, or shows
 * a friendly "coming soon" toast for sections we haven't built yet.
 */
export default function Settings() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const soon = () => toast.message(t("settings.comingSoon"));

  const items = [
    { key: "account",       icon: User,        label: t("settings.account"),       onClick: () => nav("/settings/account"), divider: false },
    { key: "privacy",       icon: Shield,      label: t("settings.privacy"),       onClick: () => nav("/settings/privacy"), divider: false },
    { key: "notifications", icon: Bell,        label: t("settings.notifications"), onClick: () => nav("/settings/notifications"), divider: true },
    { key: "people",        icon: PeopleIcon,  label: t("settings.peoplePrefs"),   onClick: () => nav("/settings/people-preferences"), divider: true },
    { key: "appearance",    icon: Languages,   label: t("settings.appearance"),    onClick: () => nav("/settings/appearance"), divider: true },
    { key: "help",          icon: HelpCircle,  label: t("settings.help"),          onClick: () => nav("/settings/help"), divider: false },
    { key: "guidelines",    icon: BookOpen,    label: t("settings.guidelines"),    onClick: () => nav("/guidelines"), divider: false },
    { key: "legal",         icon: FileText,    label: t("settings.legal"),         onClick: () => nav("/legal"), divider: false },
    { key: "important-info", icon: FileText,   label: "⚠️ Important Account & Transaction Information", onClick: () => nav("/important-info"), divider: true },
    { key: "logout",        icon: LogOut,      label: t("settings.logout"),        onClick: async () => { await logout(); nav("/"); }, divider: false },
    { key: "delete",        icon: Trash2,     label: t("settings.deleteAccount"),  onClick: () => setShowDeleteModal(true), divider: false, danger: true },
  ];

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="settings-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-5">
          <button
            onClick={() => nav(-1)}
            className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]"
            data-testid="settings-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-7 h-7" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[34px] tracking-tight">{t("settings.title")}</h1>
        </div>
      </div>

      {/* Menu list */}
      <ul className="divide-y divide-[#F0EDE3]">
        {items.map((it) => (
          <li key={it.key}>
            <button
              onClick={it.onClick}
              className="w-full flex items-center gap-4 px-5 py-5 text-left hover:bg-[#FAF8F2] active:bg-[#F4F1E8] transition-colors"
              data-testid={`settings-${it.key}`}
            >
              <it.icon className={`w-6 h-6 shrink-0 ${it.danger ? "text-[#D14848]" : "text-[#3D424B]"}`} strokeWidth={2} />
              <span className={`font-display text-lg ${it.danger ? "text-[#D14848] font-bold" : "font-semibold"}`}>{it.label}</span>
            </button>
            {it.divider && <div className="h-3 bg-[#F4F1E8]" aria-hidden="true" />}
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className="text-center pt-12 pb-8 px-5">
        <p className="font-display font-black text-xl tracking-tight text-[#0A0B0D]">
          💙 MADE WITH LOVE 💙
        </p>
        <p className="text-xs text-[#7C828D] mt-1 font-bold tracking-widest uppercase">
          TANTA · MUSTI
        </p>
      </div>

      {/* Delete-account confirmation */}
      {showDeleteModal && (
        <DeleteAccountModal
          onClose={() => setShowDeleteModal(false)}
          onConfirmed={async () => {
            setShowDeleteModal(false);
            await logout();
            nav("/", { replace: true });
          }}
        />
      )}
    </div>
  );
}

export function DeleteAccountModal({ onClose, onConfirmed }) {
  const { t } = useTranslation();
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const CONFIRM_WORD = "DELETE";

  const run = async () => {
    setBusy(true);
    try {
      await api.delete("/users/me");
      toast.success(t("settings.deleteSuccess"));
      onConfirmed();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="delete-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-[#FFE6E0] flex items-center justify-center shrink-0">
            <Trash2 className="w-6 h-6 text-[#D14848]" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="font-display font-black text-xl text-[#D14848]">{t("settings.deleteTitle")}</h2>
            <p className="text-sm text-[#3D424B] mt-1">{t("settings.deleteBody")}</p>
          </div>
        </div>

        <ul className="text-sm text-[#3D424B] space-y-1.5 mb-4 list-disc list-inside">
          <li>{t("settings.deleteLoss1")}</li>
          <li>{t("settings.deleteLoss2")}</li>
          <li>{t("settings.deleteLoss3")}</li>
        </ul>

        <div className="bg-[#FFE6E0] border-2 border-[#FF7A6B] rounded-2xl p-3 mb-4 text-center">
          <p className="text-xs font-display font-extrabold text-[#D14848] leading-tight">
            ⚠️ STRICT NO-REFUND POLICY: Account deletion is permanent and final. Any remaining T-Coins, active VIP subscription time, or purchased items are forfeited immediately with NO REFUNDS.
          </p>
        </div>

        <label className="block text-xs font-bold text-[#7C828D] uppercase tracking-wide mb-1.5">
          {t("settings.deleteConfirmLabel", { word: CONFIRM_WORD })}
        </label>
        <input
          type="text"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder={CONFIRM_WORD}
          autoFocus
          className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7] mb-4"
          data-testid="delete-confirm-input"
        />

        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black font-bold py-3 rounded-2xl"
            data-testid="delete-cancel"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={run}
            disabled={busy || confirm !== CONFIRM_WORD}
            className="flex-1 bg-[#D14848] hover:bg-[#B83333] text-white font-bold py-3 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="delete-confirm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={3} />}
            {t("settings.deletePermanent")}
          </button>
        </div>
      </div>
    </div>
  );
}
